import { useState, useEffect, useRef } from "react"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { Button } from "~/react/ui/button"
import { SheetHeader, SheetTitle, SheetFooter } from "~/react/ui/sheet"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogCloseButton,
  DialogBody,
  DialogFooter,
} from "~/react/ui/dialog"
import { Tabs, TabsList, TabsListItem, TabsTrigger, TabsContent } from "~/react/ui/tabs"
import { Icon } from "~/react/ui/icon"
import { Tag } from "~/react/ui/tag"
import { getDomainStatusInfo } from "~/react/components/email_accounts/domain_status"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuAction,
  DropdownMenuItem,
} from "~/react/ui/dropdown-menu"
import { getCsrfToken, formatDate } from "~/lib/utils"
import {
  email_domains_health_checks_path,
  email_accounts_mailboxes_path,
  checkout_upgrade_plan_path,
} from "~/lib/routes"
import type { DomainData } from "./domains_filters"
import HealthTab from "./tabs/health_tab"
import DomainDetailsTab from "./tabs/domain_details_tab"
import { Popover, PopoverTrigger, PopoverContent } from "~/react/ui/popover"

type DomainSidepanelProps = {
  domain: DomainData
  onHealthCheckTriggered?: () => void
  onDomainUpdated?: (updatedDomain: DomainData) => void
  allowEmailProvisioning?: boolean
  isPremium?: boolean
}

type FormData = {
  redirectUrl: string
  owner: {
    first_name: string
    last_name: string
    email: string
    company: string
    address: string
    postal_code: string
    city: string
    country: string
  }
}

export default function DomainSidepanel({
  domain: initialDomain,
  onHealthCheckTriggered,
  onDomainUpdated,
  allowEmailProvisioning = false,
  isPremium = false,
}: DomainSidepanelProps) {
  const [domain, setDomain] = useState<DomainData>(initialDomain)
  const [isRunningHealthCheck, setIsRunningHealthCheck] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [upgradePopoverOpen, setUpgradePopoverOpen] = useState(false)
  const [isCancellingRenewal, setIsCancellingRenewal] = useState(false)
  const [activeTab, setActiveTab] = useState(initialDomain.type === "managed" ? "details" : "email_accounts")
  const [redirectUrlTouched, setRedirectUrlTouched] = useState(false)
  const [redirectUrlLogo, setRedirectUrlLogo] = useState<string | null>(null)
  const [confirmCancelRenewalOpen, setConfirmCancelRenewalOpen] = useState(false)
  const cancelRenewalDialogRef = useRef<HTMLDialogElement>(null)

  const [formData, setFormData] = useState<FormData>({
    redirectUrl: "",
    owner: {
      first_name: "",
      last_name: "",
      email: "",
      company: "",
      address: "",
      postal_code: "",
      city: "",
      country: "",
    },
  })

  // Track previous health_check_status to detect completion
  const prevHealthCheckStatusRef = useRef(domain.health_check_status)

  // Also check health_check_status from API
  const isHealthCheckInProgress = isRunningHealthCheck || domain.health_check_status === "pending"

  // Clear isRunningHealthCheck when health check completes (transitions from pending to non-pending)
  useEffect(() => {
    const prevStatus = prevHealthCheckStatusRef.current
    const currentStatus = domain.health_check_status

    // Only clear if we were pending and now we're not
    if (isRunningHealthCheck && prevStatus === "pending" && currentStatus !== "pending") {
      setIsRunningHealthCheck(false)
    }

    prevHealthCheckStatusRef.current = currentStatus
  }, [isRunningHealthCheck, domain.health_check_status])

  // Keep domain state in sync when parent updates it (e.g., via push)
  useEffect(() => {
    setDomain((prev) => {
      // Don't revert from completed (null) back to pending
      // This prevents flickering from race conditions with ActionCable messages
      if (prev.health_check_status === null && initialDomain.health_check_status === "pending") {
        return prev
      }
      return initialDomain
    })
  }, [initialDomain])

  // Fetch full domain data on mount
  useEffect(() => {
    const fetchDomainData = async () => {
      try {
        const response = await fetch(`/email-domains/${encodeURIComponent(initialDomain.domain)}`, {
          headers: {
            Accept: "application/json",
          },
          credentials: "same-origin",
        })

        if (response.ok) {
          const data = await response.json()
          setDomain(data)

          // Initialize form with fetched data
          const newFormData: FormData = {
            redirectUrl: data.redirect_url || "",
            owner: {
              first_name: data.owner?.first_name || "",
              last_name: data.owner?.last_name || "",
              email: data.owner?.email || "",
              company: data.owner?.company || "",
              address: data.owner?.address || "",
              postal_code: data.owner?.postal_code || "",
              city: data.owner?.city || "",
              country: data.owner?.country || "",
            },
          }
          setFormData(newFormData)

          // Set logo if redirect URL exists
          if (data.redirect_url) {
            try {
              const urlObj = new URL(data.redirect_url)
              const urlDomain = urlObj.hostname.replace("www.", "")
              setRedirectUrlLogo(`https://company-logo.hunter.io/${urlDomain}`)
            } catch {
              setRedirectUrlLogo(null)
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch domain data:", error)
      }
    }

    fetchDomainData()
  }, [initialDomain.domain])

  // Update active tab if details tab is not available for non-managed domains
  useEffect(() => {
    if (domain.type !== "managed" && activeTab === "details") {
      setActiveTab("email_accounts")
    }
  }, [domain.type, activeTab])

  // Handle dialog open/close for cancel renewal dialog
  useEffect(() => {
    if (confirmCancelRenewalOpen) {
      cancelRenewalDialogRef.current?.showModal()
    } else {
      cancelRenewalDialogRef.current?.close()
    }
  }, [confirmCancelRenewalOpen])

  const statusInfo = getDomainStatusInfo(domain.status)

  const handleRunHealthCheck = async () => {
    // Immediately update domain state to show pending status (same as header button)
    const pendingDomain = { ...domain, health_check_status: "pending" as const }
    setDomain(pendingDomain)

    if (onDomainUpdated) {
      onDomainUpdated(pendingDomain)
    }

    setIsRunningHealthCheck(true)
    try {
      const response = await fetch(email_domains_health_checks_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          domain: domain.domain,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        throw new Error(error.error || i18n.t("js.email_accounts_center.errors.health_check_failed"))
      }

      await response.json()

      if (onHealthCheckTriggered) {
        onHealthCheckTriggered()
      }
    } catch (error) {
      console.error("Health check failed:", error)
      App.displayFlash(
        "error",
        error instanceof Error ? error.message : i18n.t("js.email_accounts_center.errors.health_check_failed"),
      )
      setIsRunningHealthCheck(false)
    }
  }

  const handleCancelRenewal = () => {
    if (domain.type !== "managed") return
    setConfirmCancelRenewalOpen(true)
  }

  const executeCancelRenewal = async () => {
    setIsCancellingRenewal(true)
    try {
      const response = await fetch(`/email-domains/${encodeURIComponent(domain.domain)}/subscription`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        let mailboxCount = 0

        // Response is JSON
        const data = await response.json().catch(() => ({ mailbox_count: 0 }))
        mailboxCount = data.mailbox_count || 0

        App?.displayFlash(
          "success",
          i18n.t("js.email_accounts_center.domain.cancel_renewal_success", {
            count: mailboxCount,
          }),
        )

        // Notify parent to refresh
        if (onDomainUpdated) {
          onDomainUpdated({ ...domain, status: domain.status })
        }
      } else {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        App?.displayFlash("error", error.error || i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
      }
    } catch (err) {
      App?.displayFlash("error", i18n.t("js.email_accounts_center.bulk.cancel_renewal_error"))
    } finally {
      setIsCancellingRenewal(false)
      setConfirmCancelRenewalOpen(false)
    }
  }

  const isValidUrl = (url: string): boolean => {
    if (!url) return true
    try {
      const urlObj = new URL(url)
      return urlObj.protocol === "http:" || urlObj.protocol === "https:"
    } catch {
      return false
    }
  }

  const isRedirectUrlInvalid =
    redirectUrlTouched && formData.redirectUrl.length > 0 && !isValidUrl(formData.redirectUrl)

  const handleRedirectUrlBlur = () => {
    setRedirectUrlTouched(true)
    if (formData.redirectUrl) {
      try {
        const urlObj = new URL(formData.redirectUrl)
        const urlDomain = urlObj.hostname.replace("www.", "")
        setRedirectUrlLogo(`https://company-logo.hunter.io/${urlDomain}`)
      } catch {
        setRedirectUrlLogo(null)
      }
    } else {
      setRedirectUrlLogo(null)
    }
  }

  const clearRedirectUrl = () => {
    setFormData((prev) => ({ ...prev, redirectUrl: "" }))
    setRedirectUrlLogo(null)
  }

  const updateOwnerField = (field: keyof FormData["owner"], value: string) => {
    setFormData((prev) => ({
      ...prev,
      owner: { ...prev.owner, [field]: value },
    }))
  }

  const handleSave = async () => {
    if (domain.type !== "managed") {
      // Can only update managed domains
      return
    }

    setIsSaving(true)
    try {
      const response = await fetch(`/email-domains/${encodeURIComponent(domain.domain)}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          redirect_url: formData.redirectUrl,
          owner: formData.owner,
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: i18n.t("js.unknown_error") }))
        throw new Error(error.error || i18n.t("js.email_accounts_center.domain.details.save_error"))
      }

      const updatedDomain = await response.json()
      setDomain(updatedDomain)

      if (onDomainUpdated) {
        onDomainUpdated(updatedDomain)
      }

      App.displayFlash("success", i18n.t("js.email_accounts_center.domain.details.save_success"))
    } catch (error) {
      App.displayFlash(
        "error",
        error instanceof Error ? error.message : i18n.t("js.email_accounts_center.domain.details.save_error"),
      )
    } finally {
      setIsSaving(false)
    }
  }

  const createdAt = formatDate(domain.created_at)
  const expireAt = formatDate(domain.expire_at)
  const cancellationDate = formatDate(domain.cancelation_date)

  const dateParts: string[] = []
  if (createdAt) {
    dateParts.push(`${i18n.t("js.email_accounts_center.domain.details.created_at")} ${createdAt}`)
  }
  if (expireAt && !cancellationDate) {
    dateParts.push(`${i18n.t("js.email_accounts_center.domain.details.renew_on")} ${expireAt}`)
  }
  if (cancellationDate) {
    dateParts.push(`${i18n.t("js.email_accounts_center.domain.details.cancels_on")} ${cancellationDate}`)
  }

  const baselineText = dateParts.join(" · ")
  const isPending = domain.status === "pending"

  return (
    <article>
      <div className="lead-details">
        <SheetHeader className="p-0">
          <header className="lead-details__header">
            <div className="lead-details__heading">
              <SheetTitle className="lead-details__name mb-0">{domain.domain}</SheetTitle>
              <div className="lead-details__baseline">
                <span className="lead-details__baseline-item">{baselineText}</span>
              </div>
            </div>
          </header>
        </SheetHeader>

        <div className="lead-details__actions mb-4 d-flex align-items-center gap-2">
          <Tag variant={statusInfo.variant}>{statusInfo.label}</Tag>
          {domain.provider && (
            <Tag>
              {i18n.t(`js.email_accounts_center.domains_table.providers.${domain.provider}`) || domain.provider}
            </Tag>
          )}
        </div>

        <div className="lead-details__actions d-flex align-items-center gap-2">
          {domain.type === "managed" && allowEmailProvisioning && (
            <Button size="sm" type="button" asChild disabled={isPending}>
              <a
                href={isPending ? undefined : email_accounts_mailboxes_path()}
                data-turbo="false"
                className={isPending ? "disabled" : ""}
              >
                {i18n.t("js.email_accounts_center.domain.actions.add_email_accounts")}
              </a>
            </Button>
          )}

          {isPremium ? (
            <Button
              size="sm"
              type="button"
              onClick={handleRunHealthCheck}
              disabled={isHealthCheckInProgress || isPending}
              data-loading={isHealthCheckInProgress ? "true" : "none"}
            >
              <Icon
                icon={isHealthCheckInProgress ? "spinner-third" : "heart-pulse"}
                variant="regular"
                size="sm"
                loading={isHealthCheckInProgress}
                aria-hidden="true"
              />
              {isHealthCheckInProgress
                ? i18n.t("js.email_accounts_center.global_health.checking")
                : i18n.t("js.email_accounts_center.email_accounts_table.actions.run_health_check")}
            </Button>
          ) : (
            <Popover open={upgradePopoverOpen} onOpenChange={setUpgradePopoverOpen}>
              <PopoverTrigger asChild>
                <Button size="sm" type="button">
                  <Icon icon="heart-pulse" variant="regular" size="sm" aria-hidden="true" />
                  {i18n.t("js.email_accounts_center.email_accounts_table.actions.run_health_check")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="popover-body">
                <div className="premium-feature-popover">
                  <strong>{i18n.t("js.email_accounts_center.domain.health.upgrade_title")}</strong>
                  <p className="text-sm">{i18n.t("js.email_accounts_center.domain.health.upgrade_description")}</p>
                  <Button variant="primary" asChild fullWidth>
                    <a
                      href={checkout_upgrade_plan_path({
                        needed_credits: 1,
                        needed_level: 1,
                        operation: "health_report",
                        direct_route: true,
                        format: "turbo_stream",
                      })}
                      data-turbo-stream="true"
                    >
                      <Icon icon="arrow-alt-circle-up" size="sm" />
                      {i18n.t("js.email_accounts_center.domain.health.upgrade_cta")}
                    </a>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {domain.type === "managed" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  iconOnly
                  type="button"
                  aria-label={i18n.t("js.email_accounts_center.actions.more_actions_aria")}
                >
                  <Icon icon="ellipsis-h" variant="regular" size="sm" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="dropdown-menu-end">
                <DropdownMenuItem>
                  <DropdownMenuAction type="button" onClick={handleCancelRenewal} disabled={isPending} variant="danger">
                    {i18n.t("js.email_accounts_center.email_accounts_table.actions.cancel_renewal")}
                  </DropdownMenuAction>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          // Prevent switching to health tab for non-premium users
          if (value === "health" && !isPremium) {
            return
          }
          setActiveTab(value)
        }}
      >
        <TabsList color="lightergrey" size="md" className="pt-6 px-6">
          {domain.type === "managed" && (
            <TabsListItem>
              <TabsTrigger value="details">{i18n.t("js.email_accounts_center.domain.details.title")}</TabsTrigger>
            </TabsListItem>
          )}
          <TabsListItem>
            <TabsTrigger value="email_accounts">
              {i18n.t("js.email_accounts_center.domain.email_accounts.title")}
            </TabsTrigger>
          </TabsListItem>
          {isPremium && (
            <TabsListItem>
              <TabsTrigger value="health">{i18n.t("js.email_accounts_center.domain.health.title")}</TabsTrigger>
            </TabsListItem>
          )}
        </TabsList>

        {domain.type === "managed" && (
          <TabsContent value="details" className="p-6">
            <DomainDetailsTab
              redirectUrl={formData.redirectUrl}
              redirectUrlLogo={redirectUrlLogo}
              isRedirectUrlInvalid={isRedirectUrlInvalid}
              owner={formData.owner}
              onRedirectUrlChange={(value) => {
                setFormData((prev) => ({ ...prev, redirectUrl: value }))
                if (!value) {
                  setRedirectUrlTouched(false)
                }
              }}
              onRedirectUrlBlur={handleRedirectUrlBlur}
              onRedirectUrlClear={() => {
                clearRedirectUrl()
                setRedirectUrlTouched(false)
              }}
              onOwnerFieldChange={updateOwnerField}
              i18n={i18n}
            />
          </TabsContent>
        )}

        <TabsContent value="email_accounts" className="p-6">
          <h3
            className="text-sm mb-4"
            dangerouslySetInnerHTML={{
              __html: i18n.t("js.email_accounts_center.domain.email_accounts.count_html", {
                pretty_count: CurrencyHelper.numberWithDelimiter(domain.email_accounts_count),
                count: domain.email_accounts_count,
              }),
            }}
          />
          <ul className="lead-campaigns">
            {domain.email_accounts && domain.email_accounts.length > 0 ? (
              domain.email_accounts.map((email) => (
                <li key={email} className="lead-campaign">
                  <div className="lead-campaign__heading">
                    <strong className="lead-campaign__name">{email}</strong>
                    <div className="lead-campaign__metas">{/* TODO: Add owner name */}</div>
                  </div>
                  <Tag variant={domain.status === "pending" ? "warning" : "success"}>
                    {i18n.t(`js.email_accounts_center.domains_table.status.${domain.status}`)}
                  </Tag>
                </li>
              ))
            ) : (
              <li className="text-xs text-body-secondary">
                {i18n.t("js.email_accounts_center.domain.email_accounts.empty")}
              </li>
            )}
          </ul>
        </TabsContent>

        <TabsContent value="health" className="p-0">
          {domain.domain && (
            <HealthTab
              domain={domain.domain}
              health={domain.score ?? null}
              healthCheckStatus={domain.health_check_status}
              isPremium={isPremium}
              onRunHealthCheck={handleRunHealthCheck}
            />
          )}
        </TabsContent>
      </Tabs>

      {activeTab === "details" && domain.type === "managed" && (
        <SheetFooter>
          <Button
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSaving || isRedirectUrlInvalid}
            data-loading={isSaving ? "true" : "none"}
          >
            {i18n.t("js.email_accounts_center.domain.details.save")}
          </Button>
        </SheetFooter>
      )}

      {/* Confirmation Dialog */}
      {confirmCancelRenewalOpen && (
        <Dialog ref={cancelRenewalDialogRef}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{i18n.t("js.email_accounts_center.domain.cancel_renewal_title")}</DialogTitle>
              <DialogCloseButton onClick={() => setConfirmCancelRenewalOpen(false)} />
            </DialogHeader>
            <DialogBody>
              <p
                dangerouslySetInnerHTML={{
                  __html: expireAt
                    ? i18n.t("js.email_accounts_center.domain.cancel_renewal_confirm_html", {
                        expiration_date: expireAt,
                      })
                    : i18n.t("js.email_accounts_center.domain.cancel_renewal_confirm_no_date_html"),
                }}
              />
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmCancelRenewalOpen(false)} disabled={isCancellingRenewal}>
                {i18n.t("js.email_accounts_center.domain.cancel_renewal_keep")}
              </Button>
              <Button
                variant="danger"
                onClick={executeCancelRenewal}
                disabled={isCancellingRenewal}
                data-loading={isCancellingRenewal ? "true" : "none"}
              >
                {i18n.t("js.email_accounts_center.domain.cancel_renewal_cta")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </article>
  )
}
